import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import PilotCodeOfConductText from '../components/PilotCodeOfConductText';
import './PilotRegistrationPage.css';

function localDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PilotRegistrationPage = () => {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    phone: ''
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [sepRatingValidUntil, setSepRatingValidUntil] = useState('');
  const [medicalFile, setMedicalFile] = useState(null);
  const [medicalValidUntil, setMedicalValidUntil] = useState('');
  const [logbookFile, setLogbookFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pilotCodeAccepted, setPilotCodeAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.username?.trim()) {
      setError(t('pilot.errUsername'));
      return;
    }
    if (!formData.password) {
      setError(t('pilot.errPassword'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('pilot.errPasswordLength'));
      return;
    }
    if (!formData.name?.trim()) {
      setError(t('pilot.errName'));
      return;
    }
    if (!formData.email?.trim()) {
      setError(t('pilot.errEmail'));
      return;
    }
    if (!licenseFile) {
      setError(t('pilot.errLicense'));
      return;
    }
    if (!sepRatingValidUntil?.trim()) {
      setError(t('pilot.errSepUntilRequired'));
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(sepRatingValidUntil.trim())) {
      setError(t('pilot.errSepUntilInvalid'));
      return;
    }
    if (sepRatingValidUntil.trim() < localDateString()) {
      setError(t('pilot.errSepUntilPast'));
      return;
    }
    if (!medicalFile) {
      setError(t('pilot.errMedical'));
      return;
    }
    if (!medicalValidUntil?.trim()) {
      setError(t('pilot.errMedicalUntilRequired'));
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(medicalValidUntil.trim())) {
      setError(t('pilot.errMedicalUntilInvalid'));
      return;
    }
    if (medicalValidUntil.trim() < localDateString()) {
      setError(t('pilot.errMedicalUntilPast'));
      return;
    }
    if (!logbookFile) {
      setError(t('pilot.errLogbook'));
      return;
    }
    if (!pilotCodeAccepted) {
      setError(t('pilot.errConductRequired'));
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(licenseFile.type)) {
      setError(t('pilot.errLicenseFormat'));
      return;
    }
    if (!allowedTypes.includes(medicalFile.type)) {
      setError(t('pilot.errMedicalFormat'));
      return;
    }
    if (!allowedTypes.includes(logbookFile.type)) {
      setError(t('pilot.errLogbookFormat'));
      return;
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('username', formData.username.trim());
      data.append('password', formData.password);
      data.append('name', formData.name.trim());
      data.append('email', formData.email.trim());
      data.append('phone', formData.phone?.trim() || '');
      data.append('license', licenseFile);
      data.append('sep_rating_valid_until', sepRatingValidUntil.trim());
      data.append('medical', medicalFile);
      data.append('medical_valid_until', medicalValidUntil.trim());
      data.append('logbook', logbookFile);
      data.append('pilot_code_accepted', '1');

      const res = await axios.post('/api/auth/register-pilot', data, {
        timeout: 120000,
        maxContentLength: 20 * 1024 * 1024,
        maxBodyLength: 20 * 1024 * 1024
      });
      setSuccess(res.data.message || t('pilot.success'));
      setFormData({ username: '', password: '', name: '', email: '', phone: '' });
      setLicenseFile(null);
      setSepRatingValidUntil('');
      setMedicalFile(null);
      setMedicalValidUntil('');
      setLogbookFile(null);
      setPilotCodeAccepted(false);
      formRef.current?.reset();
    } catch (err) {
      const status = err.response?.status;
      if (status === 502 || status === 503 || status === 504) {
        setError(t('pilot.errGateway'));
      } else if (err.code === 'ECONNABORTED') {
        setError(t('pilot.errTimeout'));
      } else {
        setError(err.response?.data?.error || err.message || t('pilot.errGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pilot-registration-page container">
      <h1>{t('pilot.title')}</h1>

      <div className="pilot-reg-intro card">
        <p>{t('pilot.intro1')}</p>
        <p>{t('pilot.intro2')}</p>
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="card pilot-reg-form-card">
        <h2>{t('pilot.formTitle')}</h2>
        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label>{t('pilot.username')} {t('common.required')}</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              placeholder={t('pilot.placeholderUsername')}
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label>{t('pilot.password')} {t('common.required')}</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              placeholder={t('pilot.placeholderPassword')}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label>{t('pilot.fullName')} {t('common.required')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder={t('pilot.placeholderName')}
              autoComplete="name"
            />
          </div>
          <div className="form-group">
            <label>{t('pilot.email')} {t('common.required')}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder={t('pilot.placeholderEmail')}
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label>{t('pilot.phone')}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+36 30 123 4567"
              autoComplete="tel"
            />
          </div>
          <div className="form-group">
            <label>{t('pilot.license')} {t('common.required')}</label>
            <div className="pilot-license-sep-row">
              <div className="pilot-license-file">
                <input
                  id="license-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setLicenseFile(e.target.files?.[0] || null)}
                  required
                />
                {licenseFile && <span className="file-name">{licenseFile.name}</span>}
              </div>
              <div className="pilot-sep-until">
                <label htmlFor="sep-rating-valid-until">{t('pilot.sepRatingValidUntil')} {t('common.required')}</label>
                <input
                  id="sep-rating-valid-until"
                  type="date"
                  value={sepRatingValidUntil}
                  onChange={(e) => setSepRatingValidUntil(e.target.value)}
                  min={localDateString()}
                  required
                />
                <p className="pilot-field-hint pilot-sep-until-hint">{t('pilot.sepRatingValidUntilHint')}</p>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>{t('pilot.medical')} {t('common.required')}</label>
            <div className="pilot-license-sep-row">
              <div className="pilot-license-file">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setMedicalFile(e.target.files?.[0] || null)}
                  required
                />
                {medicalFile && <span className="file-name">{medicalFile.name}</span>}
              </div>
              <div className="pilot-sep-until">
                <label htmlFor="medical-valid-until">{t('pilot.medicalValidUntil')} {t('common.required')}</label>
                <input
                  id="medical-valid-until"
                  type="date"
                  value={medicalValidUntil}
                  onChange={(e) => setMedicalValidUntil(e.target.value)}
                  min={localDateString()}
                  required
                />
                <p className="pilot-field-hint pilot-sep-until-hint">{t('pilot.medicalValidUntilHint')}</p>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>{t('pilot.logbook')} {t('common.required')}</label>
            <p className="pilot-field-hint">{t('pilot.logbookHint')}</p>
            <input
              id="logbook-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setLogbookFile(e.target.files?.[0] || null)}
              required
            />
            {logbookFile && <span className="file-name">{logbookFile.name}</span>}
          </div>
          <div className="form-group pilot-reg-conduct-block">
            <PilotCodeOfConductText />
            <label className="pilot-reg-conduct-accept">
              <input
                type="checkbox"
                checked={pilotCodeAccepted}
                onChange={(e) => setPilotCodeAccepted(e.target.checked)}
              />{' '}
              {t('pilot.conductAcceptLabel')}
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? t('pilot.submitting') : t('pilot.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PilotRegistrationPage;
